import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Layouts
import AdminLayout from './layouts/admin/AdminLayout.jsx';
import MainLayout from './layouts/MainLayout.jsx';

// Admin Pages
import AdminAuthComplete from './pages/admin/auth/AdminAuthComplete.js';
import AdminLoginPage from './pages/admin/auth/AdminLoginPage.js';
import AdminRegisterPage from './pages/admin/auth/AdminRegisterPage.js';
import AdminDashboard from './pages/admin/AdminDashboardPage/AdminDashboardPage.js';
import AdminProducts from './pages/admin/admin-products/AdminProductsPage/AdminProductsPage.js';
import AdminProductEditor from './pages/admin/admin-products/AdminProductEditor/AdminProductEditor.js';
import AdminOrders from './pages/admin/admin-orders/AdminOrdersPage/AdminOrdersPage.js';
import AdminCategories from './pages/admin/AdminCategoriesPage/AdminCategoriesPage.js';
import AdminSettings from './pages/admin/AdminSettingsPage/AdminSettingsPage.js';
import AdminCustomers from './pages/admin/AdminCustomersPage/AdminCustomersPage.js';
import OrderDetailsView from './pages/admin/admin-orders/OrderDetailsView/OrderDetailsView.js'
import OrderInvoice from './pages/admin/admin-orders/AdminOrderInvoice/AdminOrderInvoice.js'

// User Pages
import HomePage from './pages/customer/HomePage/HomePage.js';
import ShopPage from './pages/customer/ShopPage/ShopPage.js';
import ItemDetail from './pages/customer/ItemDetailPage/ItemDetailPage.js';
import AboutUs from './pages/customer/AboutUsPage/AboutUsPage.js';
import ContactUs from './pages/customer/ContactUsPage/ContactUsPage.js';
import Checkout from './pages/customer/checkout/CheckoutPage/CheckoutPage.js';
import StripeSuccessPage from './pages/customer/checkout/StripeSuccessPage.jsx';
import OrderConfirmation from './pages/customer/OrderConfirmationPage/OrderConfirmationPage.js';
import UserAccount from './pages/customer/UserAccountPage/UserAccountPage.js';
import OrderHistory from './pages/customer/OrderHistoryPage/OrderHistoryPage.js';
import WishlistPage from './pages/customer/WishlistPage/WishlistPage.js';

//Auth Components 
import AuthPage from './pages/customer/auth/AuthPage.js';
import AuthComplete from './pages/customer/auth/AuthComplete.js';

// Components
import AdminPrivateRoute from './pages/admin/routes/AdminPrivateRoute.jsx';
import UserPrivateRoute from './pages/customer/routes/UserPrivateRoute.jsx';
import ScrollToTop from './components/common/ScrollToTop/ScrollToTop.js';
import ScrollManager from './components/common/ScrollManager/ScrollManager.js';
import DocumentHead from './components/common/DocumentHead/DocumentHead.js';
import BrandColorsInjector from './components/common/BrandColorsInjector/BrandColorsInjector.js';
import SessionValidator from './components/common/SessionValidator/SessionValidator.js';
// import AdminInventoryTracker from './pages/admin/AdminInventoryTracker.js';


// We're importing these AFTER all other imports to prevent circular dependencies
import { UserProvider } from './context/UserContext.js';
import { CartProvider } from './context/CartContext.js';
import { FirebaseProvider } from './context/FirebaseContext.js';
import { LanguageProvider } from './context/LanguageContext.js';

const App = () => {
  // In development, show the email status component
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      // This will create the email status component in the DOM
      const cleanup = () => {
        // Find and remove the component if it exists
        const component = document.querySelector('[data-email-status]');
        if (component) {
          document.body.removeChild(component);
        }
      };



      return cleanup;
    }
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <ScrollManager />
      <BrandColorsInjector />
      <ToastContainer position="top-right" autoClose={5000} />

      {/* Nest providers in the correct order of dependency */}
      <LanguageProvider>
        <FirebaseProvider>
          <UserProvider>
            <SessionValidator awayTimeMinutes={7}>
              <CartProvider>
                <DocumentHead />
                <Routes>
                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin/register" element={<AdminRegisterPage />} />
                  <Route path="/admin/auth/complete" element={<AdminAuthComplete />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route element={<AdminPrivateRoute />}>
                      <Route index element={<Navigate to="/admin/dashboard" replace />} />
                      <Route path="dashboard" element={<AdminDashboard />} />
                      {/* <Route path="inventory" element={<AdminInventoryTracker />} /> */}
                      <Route path="products" element={<AdminProducts />} />
                      <Route path="products/new" element={<AdminProductEditor />} />
                      <Route path="products/edit/:id" element={<AdminProductEditor />} />
                      <Route path="orders" element={<AdminOrders />} />
                      <Route path="orders/:orderId" element={<OrderDetailsView />} />
                      <Route path="orders/:orderId/invoice" element={<OrderInvoice />} />
                      <Route path="categories" element={<AdminCategories />} />
                      <Route path="categories/new" element={<AdminCategories />} />
                      <Route path="categories/edit/:categoryId" element={<AdminCategories />} />
                      <Route path="settings" element={<AdminSettings />} />
                      <Route path="customers" element={<AdminCustomers />} />
                    </Route>
                    {/* Catch-all for any admin routes not protected above */}
                    <Route path="*" element={<Navigate to="/admin/login" replace />} />
                  </Route>

                  {/* Public User Routes */}
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/shop" element={<ShopPage />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/contact" element={<ContactUs />} />

                    {/* New unified auth routes */}
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/auth/complete" element={<AuthComplete />} />

                    {/* Legacy routes - redirect to new auth page */}
                    <Route path="/login" element={<Navigate to="/auth" replace />} />
                    <Route path="/register" element={<Navigate to="/auth" replace />} />


                    {/* Enhanced Product Routes */}
                    {/* Primary route: /product/sku-or-name with optional variant */}
                    <Route path="/product/:productSlug" element={<ItemDetail />} />

                    {/* Legacy route: redirect old ID-based URLs */}
                    <Route path="/products/:productId" element={<ItemDetail />} />

                    <Route path="/collection/:categoryId" element={<ShopPage />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/stripe-success" element={<StripeSuccessPage />} />
                    <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />

                    {/* Protected User Routes */}
                    <Route element={<UserPrivateRoute />}>
                      <Route path="/account" element={<UserAccount />} />
                      <Route path="/orders" element={<OrderHistory />} />
                      <Route path="/wishlist" element={<WishlistPage />} />
                    </Route>
                  </Route>

                  {/* Catch all - Redirect to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </CartProvider>
            </SessionValidator>
          </UserProvider>
        </FirebaseProvider>
      </LanguageProvider>
    </Router>
  );
};

export default App;